// Site navigation aside (Figma node 33:409 "Navigation"): a single toggle opens/closes the
// whole aside, and each parent item ("Directory Head" in Figma) drills into its own level
// rather than expanding inline - only one level is visible at a time, matched by
// data-site-nav-level="<id>" against the button's data-site-nav-drill-into="<id>". All levels
// are rendered server-side up front (see _MainNavigationLevel.cshtml); this only toggles which
// one has the `hidden` attribute removed, plus the open/closed state of the aside itself.
//
// The back arrow lives once in the shared header (not per-level, unlike the old design) -
// see _MainNavigation.cshtml's [data-site-nav-back] button - since Figma places it there
// alongside the close icon, not inside each level's own content. It's only shown once drilled
// past the root level, and going back needs to know which level is *currently* visible (found
// via [data-site-nav-level]:not([hidden])) rather than closest()-ing from the button itself,
// since the button no longer lives inside any level. Each level names the one it goes back to in
// data-site-nav-parent: levels are rendered as siblings, not nested, so it can't be found by
// walking up the DOM (that sent every back to the top level).
//
// `root` (the [data-site-nav] element, .site-nav-root) is where [data-open] is set - CSS keys
// every open/collapsed style off .site-nav-root[data-open="true"] descendant selectors
// (layout/site-navigation.css), not off .site-nav directly, since .site-nav__header is a
// sibling of .site-nav that also needs to react to the same state. Desktop vs. mobile layout
// (push vs. overlay) is pure CSS from there - this module doesn't need to know which mode is
// active.

function setOpen(root: HTMLElement, open: boolean): void {
    root.dataset.open = open ? "true" : "false";
    document.querySelectorAll<HTMLButtonElement>("[data-site-nav-toggle]").forEach((toggle) => {
        toggle.setAttribute("aria-expanded", String(open));
    });
    document.querySelectorAll<HTMLElement>("[data-site-nav-icon-open]").forEach((icon) => icon.toggleAttribute("hidden", open));
    document.querySelectorAll<HTMLElement>("[data-site-nav-icon-close]").forEach((icon) => icon.toggleAttribute("hidden", !open));
    document.querySelector<HTMLElement>("[data-site-nav-scrim]")?.toggleAttribute("data-open", open);
}

function showLevel(root: HTMLElement, levelId: string): void {
    root.querySelectorAll<HTMLElement>("[data-site-nav-level]").forEach((level) => {
        level.hidden = level.dataset.siteNavLevel !== levelId;
    });
    root.querySelector<HTMLElement>("[data-site-nav-back]")?.toggleAttribute("hidden", levelId === "root");
}

function initToggle(root: HTMLElement): void {
    document.querySelectorAll<HTMLButtonElement>("[data-site-nav-toggle]").forEach((toggle) => {
        toggle.addEventListener("click", () => {
            setOpen(root, root.dataset.open !== "true");
        });
    });
}

function initScrim(root: HTMLElement): void {
    document.querySelector<HTMLElement>("[data-site-nav-scrim]")?.addEventListener("click", () => {
        setOpen(root, false);
    });
}

// Hiding a level takes the focused drill-into/back button with it, which drops keyboard focus to
// <body> - so focus is moved to where the user is now: the new level's first link or button.
function focusFirstIn(root: HTMLElement, levelId: string): void {
    root.querySelector<HTMLElement>(`[data-site-nav-level="${CSS.escape(levelId)}"]`)
        ?.querySelector<HTMLElement>("a[href], button:not([hidden])")
        ?.focus();
}

function initBack(root: HTMLElement): void {
    root.querySelector<HTMLButtonElement>("[data-site-nav-back]")?.addEventListener("click", () => {
        const currentLevel = root.querySelector<HTMLElement>("[data-site-nav-level]:not([hidden])");
        showLevel(root, currentLevel?.dataset.siteNavParent || "root");
        // Back on the button that led into the level just left, so the user keeps their place.
        const leftId = currentLevel?.dataset.siteNavLevel;
        const drillButton = leftId ? root.querySelector<HTMLElement>(`[data-site-nav-drill-into="${CSS.escape(leftId)}"]`) : null;
        drillButton?.focus();
    });
}

function initDrillDown(root: HTMLElement): void {
    root.addEventListener("click", (event) => {
        const drillButton = (event.target as HTMLElement).closest<HTMLElement>("[data-site-nav-drill-into]");
        if (drillButton) {
            const levelId = drillButton.dataset.siteNavDrillInto ?? "";
            showLevel(root, levelId);
            focusFirstIn(root, levelId);
        }
    });
}

// Escape closes the open aside and returns focus to the toggle that opened it.
function initEscape(root: HTMLElement): void {
    document.addEventListener("keydown", (event) => {
        if (event.key !== "Escape" || root.dataset.open !== "true") {
            return;
        }
        setOpen(root, false);
        root.querySelector<HTMLButtonElement>("[data-site-nav-toggle]")?.focus();
    });
}

// _MainNavigationLevel.cshtml already renders the correct level un-hidden server-side when
// landing on a sub-page (see its own "descendantIsActive" logic), so the back arrow's initial
// visibility has to be synced to that on load - otherwise it would only appear after the next
// click, even though the menu already starts drilled in.
function initBackVisibility(root: HTMLElement): void {
    const visibleLevel = root.querySelector<HTMLElement>("[data-site-nav-level]:not([hidden])");
    root.querySelector<HTMLElement>("[data-site-nav-back]")?.toggleAttribute("hidden", visibleLevel?.dataset.siteNavLevel === "root" || !visibleLevel);
}

export function initSiteNavigation(): void {
    document.querySelectorAll<HTMLElement>("[data-site-nav]").forEach((root) => {
        initToggle(root);
        initScrim(root);
        initBack(root);
        initDrillDown(root);
        initEscape(root);
        initBackVisibility(root);
    });
}
